import { z } from 'zod';

const chatAttachmentReceiptSchema = z.object({
    attachment_id: z.string().min(1),
    filepath: z.string().min(1),
    filename: z.string().min(1),
    content_type: z.string().min(1),
    disposition: z.enum(['inline', 'attachment']),
    size_bytes: z.number().int().nonnegative(),
});

const sendChatAttachmentOutputSchema = z.object({
    chat_attachment: chatAttachmentReceiptSchema,
});

const SEND_CHAT_ATTACHMENT_TOOL_NAME = 'send_chat_attachment';

export type V3ChatAttachment = {
    readonly kind: 'chat-attachment';
    readonly version: 1;
    readonly runId: string;
    readonly attachmentId: string;
    readonly filepath: string;
    readonly filename: string;
    readonly contentType: string;
    readonly disposition: 'inline' | 'attachment';
    readonly sizeBytes: number;
};

export function parseV3ChatAttachment({
    toolName,
    runId,
    output,
}: {
    readonly toolName: string;
    readonly runId: string | null | undefined;
    readonly output: unknown;
}): V3ChatAttachment | null {
    if (toolName !== SEND_CHAT_ATTACHMENT_TOOL_NAME || !runId) {
        return null;
    }
    const parsed = sendChatAttachmentOutputSchema.safeParse(output);
    if (!parsed.success) {
        return null;
    }
    const receipt = parsed.data.chat_attachment;
    return {
        kind: 'chat-attachment',
        version: 1,
        runId,
        attachmentId: receipt.attachment_id,
        filepath: receipt.filepath,
        filename: receipt.filename,
        contentType: receipt.content_type,
        disposition: receipt.disposition,
        sizeBytes: receipt.size_bytes,
    };
}

// The backend stores only allowlisted formats. Keep the client predicate
// exact so stale or malformed receipts cannot create an inline active path.
export function isImageAttachment(attachment: V3ChatAttachment): boolean {
    if (attachment.disposition !== 'inline') {
        return false;
    }
    return new Set(['image/gif', 'image/jpeg', 'image/png', 'image/webp']).has(
        attachment.contentType.split(';', 1)[0]?.toLowerCase() ?? '',
    );
}

export function isMarkdownAttachment(attachment: V3ChatAttachment): boolean {
    if (attachment.disposition !== 'inline') {
        return false;
    }
    const contentType = attachment.contentType.toLowerCase();
    return (
        contentType === 'text/markdown' ||
        contentType.startsWith('text/markdown;')
    );
}

export function formatAttachmentSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    if (sizeBytes < 1024 * 1024) {
        const kilobytes = sizeBytes / 1024;
        return `${kilobytes.toFixed(kilobytes >= 10 ? 0 : 1)} KB`;
    }
    const megabytes = sizeBytes / (1024 * 1024);
    return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
}
